import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";
import projects from "../../data/projects";

gsap.registerPlugin(ScrollTrigger);

const Projects = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const [showAll, setShowAll] = useState(false);

  const displayedProjects = showAll
    ? [...projects].sort((a, b) => (b.year || "").localeCompare(a.year || ""))
    : projects.filter((p) => p.featured);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Heading animation
      gsap.from(headingRef.current, {
        y: 100,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headingRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      // Project cards animation
      const projectCards = document.querySelectorAll(".project-item");
      projectCards.forEach((card, index) => {
        gsap.from(card, {
          y: 100,
          opacity: 0,
          duration: 1,
          delay: index * 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [showAll]);

  return (
    <section
      ref={sectionRef}
      id="projects"
      className="relative py-32 md:py-48 bg-[#111] overflow-hidden"
    >
      {/* Background accent */}
      <div className="circle-accent top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24">
          <div>
            <span className="section-label">Selected Works</span>
            <h2 ref={headingRef} className="section-title">
              Featured<br />
              <span className="text-gradient">Projects</span>
            </h2>
          </div>

          {/* Toggle */}
          <div className="flex gap-4">
            <button
              onClick={() => setShowAll(false)}
              className={`px-6 py-3 text-sm tracking-wider uppercase rounded-full border transition-all duration-300 ${
                !showAll
                  ? "bg-[#c9ff00] text-[#0a0a0a] border-[#c9ff00]"
                  : "border-[#333] text-[#888] hover:border-[#c9ff00] hover:text-white"
              }`}
            >
              Featured
            </button>
            <button
              onClick={() => setShowAll(true)}
              className={`px-6 py-3 text-sm tracking-wider uppercase rounded-full border transition-all duration-300 ${
                showAll
                  ? "bg-[#c9ff00] text-[#0a0a0a] border-[#c9ff00]"
                  : "border-[#333] text-[#888] hover:border-[#c9ff00] hover:text-white"
              }`}
            >
              All Work
            </button>
          </div>
        </div>

        {/* Projects grid */}
        <div className="grid gap-16 md:gap-24">
          {displayedProjects.map((project, index) => (
            <div
              key={project.slug}
              className="project-item group block"
            >
              <div className={`grid md:grid-cols-2 gap-8 md:gap-16 items-center ${
                index % 2 === 1 ? "md:grid-flow-dense" : ""
              }`}>
                {/* Visual card */}
                <Link
                  to={`/project/${project.slug}`}
                  className={`relative overflow-hidden rounded-2xl aspect-[4/3] block bg-gradient-to-br from-[#111] to-[#1a1a1a] border border-[#222] group-hover:border-[#c9ff00]/30 transition-all duration-500 ${
                    index % 2 === 1 ? "md:col-start-2" : ""
                  }`}
                >
                  {/* Featured badge */}
                  {project.featured && (
                    <div className="absolute top-4 left-4 z-30 px-4 py-2 bg-[#c9ff00] text-[#0a0a0a] text-xs font-bold uppercase tracking-wider rounded-full">
                      Featured
                    </div>
                  )}
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-[#c9ff00]/5 to-[#00fff0]/5 opacity-0 group-hover:opacity-100 transition-all duration-500" />
                  <div className="absolute bottom-6 left-6 z-30">
                    <span className="text-xs tracking-wider uppercase text-[#888]">
                      {project.category}
                    </span>
                  </div>
                  {/* View project button */}
                  <div className="absolute bottom-6 right-6 z-30">
                    <div className="w-16 h-16 rounded-full bg-[#c9ff00] text-[#0a0a0a] flex items-center justify-center opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-500">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 11L11 1M11 1H1M11 1V11"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                      </svg>
                    </div>
                  </div>
                </Link>

                {/* Content */}
                <div className={`flex flex-col ${
                  index % 2 === 1 ? "md:col-start-1 md:text-right md:items-end" : ""
                }`}>
                  {/* Project number */}
                  <span className="project-number opacity-30">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  {/* Category */}
                  <div className={`flex items-center gap-4 text-sm text-[#888] mb-4 flex-wrap ${
                    index % 2 === 1 ? "md:justify-end" : ""
                  }`}>
                    <span className="text-[#c9ff00] font-medium">{project.category}</span>
                    {project.year && (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-[#333]" />
                        <span>{project.year}</span>
                      </>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-3xl md:text-5xl font-display font-bold mb-4 group-hover:text-[#c9ff00] transition-colors duration-300">
                    {project.title}
                  </h3>

                  {/* Description */}
                  <p className="text-[#888] text-lg mb-6 max-w-md leading-relaxed">
                    {project.background}
                  </p>

                  {/* Tech stack */}
                  <div className={`flex flex-wrap gap-2 mb-6 ${
                    index % 2 === 1 ? "md:justify-end" : ""
                  }`}>
                    {project.stack.map((tech, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 text-xs tracking-wider uppercase border border-[#333] rounded-full text-[#888]"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Links */}
                  <div className={`flex flex-wrap gap-3 ${index % 2 === 1 ? "md:justify-end" : ""}`}>
                    <Link
                      to={`/project/${project.slug}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#c9ff00] text-[#0a0a0a] text-sm font-medium rounded-full hover:bg-white transition-colors"
                    >
                      <span>View Details</span>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M1 11L11 1M11 1H1M11 1V11" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    </Link>
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#333] text-white text-sm font-medium rounded-full hover:border-[#c9ff00] transition-colors"
                    >
                      <span>Prototype</span>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M1 11L11 1M11 1H1M11 1V11" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View all CTA */}
        {!showAll && (
          <div className="flex justify-center mt-24">
            <button
              onClick={() => setShowAll(true)}
              className="btn-outline magnetic"
            >
              <span>View All Projects</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 11L11 1M11 1H1M11 1V11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
